import { useEffect, useRef, useState, useCallback } from 'react';
import { useApiHealthStore } from '../../stores/apiHealthStore';
import './ApiDownOverlay.css';

const CHECK_INTERVAL_MS = 30_000; // Retry every 30 seconds

/** Format elapsed seconds into a human-readable string */
function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

export default function ApiDownOverlay() {
  const { isApiDown, lastError, checkCount, isChecking, downSince, recovered, checkHealth } =
    useApiHealthStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Ticking elapsed time
  const [elapsed, setElapsed] = useState(0);
  // Countdown to next retry (0-10)
  const [countdown, setCountdown] = useState(CHECK_INTERVAL_MS / 1000);

  // Tick every second for elapsed time and countdown
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Start/stop polling when API goes down/up
  useEffect(() => {
    if (isApiDown && !recovered) {
      // Immediate check, then poll
      checkHealth();
      intervalRef.current = setInterval(checkHealth, CHECK_INTERVAL_MS);

      // 1-second tick for elapsed + countdown
      setCountdown(CHECK_INTERVAL_MS / 1000);
      tickRef.current = setInterval(() => {
        if (downSince) {
          setElapsed(Date.now() - downSince);
        }
        setCountdown((prev) => (prev <= 1 ? CHECK_INTERVAL_MS / 1000 : prev - 1));
      }, 1000);
    } else {
      clearTimers();
    }

    return clearTimers;
  }, [isApiDown, recovered, checkHealth, downSince, clearTimers]);

  if (!isApiDown) return null;

  // Determine visual state
  const iconState = recovered ? 'recovered' : isChecking ? 'checking' : 'error';
  const icon = recovered
    ? 'bi-check-circle-fill'
    : isChecking
      ? 'bi-arrow-repeat'
      : 'bi-cloud-slash';

  const title = recovered
    ? 'Connection Restored'
    : 'Server Unavailable';

  const subtitle = recovered
    ? 'The API is back online. Resuming...'
    : lastError || 'Unable to reach the server. We are trying to reconnect automatically.';

  const progressPct = isChecking ? 100 : ((CHECK_INTERVAL_MS / 1000 - countdown) / (CHECK_INTERVAL_MS / 1000)) * 100;

  return (
    <div className="api-down-backdrop">
      <div className="api-down-card">
        {/* Icon */}
        <div className={`api-down-icon-ring ${iconState}`}>
          <i className={`bi ${icon} icon`} />
        </div>

        <h2 className="api-down-title">{title}</h2>
        <p className="api-down-subtitle">{subtitle}</p>

        {!recovered && (
          <>
            {/* Status details */}
            <div className="api-down-status">
              <div className="api-down-status-row">
                <span className="api-down-status-label">
                  <span className={`api-down-status-dot ${isChecking ? 'amber' : 'red'}`} />
                  Status
                </span>
                <span className="api-down-status-value">
                  {isChecking ? 'Checking...' : 'Disconnected'}
                </span>
              </div>

              {downSince != null && (
                <div className="api-down-status-row">
                  <span className="api-down-status-label">Elapsed</span>
                  <span className="api-down-status-value">{formatElapsed(elapsed)}</span>
                </div>
              )}

              <div className="api-down-status-row">
                <span className="api-down-status-label">Attempts</span>
                <span className="api-down-status-value">{checkCount}</span>
              </div>

              <div className="api-down-status-row">
                <span className="api-down-status-label">Next retry</span>
                <span className="api-down-status-value">
                  {isChecking ? 'now' : `${countdown}s`}
                </span>
              </div>

              {/* Progress bar */}
              <div className="api-down-progress-track">
                <div
                  className="api-down-progress-bar"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {/* Manual retry */}
            <button
              className="api-down-retry-btn"
              onClick={checkHealth}
              disabled={isChecking}
            >
              {isChecking ? 'Checking...' : 'Retry Now'}
            </button>

            <p className="api-down-footer">
              Auto-retrying every {CHECK_INTERVAL_MS / 1000} seconds
            </p>
          </>
        )}
      </div>
    </div>
  );
}
